import {
  convertEmbindVectorToArray,
  PRMarinaNode,
  PRMarinaFileArtboard,
} from '@sketch-hq/sketch-web-renderer'

/**
 * The subset of a PRFile entity the canvas overlays need, so entities read from
 * the node tree can stand in for the ones the PRFile hands back.
 */
export type OverlayEntity = Pick<
  PRMarinaFileArtboard,
  'objectId' | 'name' | 'bounds' | 'getNode'
>

const toOverlayEntity = (node: PRMarinaNode): OverlayEntity => ({
  objectId: node.getLayerUUID(),
  name: node.getLayerName(),
  bounds: node.getAbsoluteBounds(),
  getNode: () => node,
})

/**
 * The entities to overlay when a Section is focused: every Section and frame it
 * contains, at any depth, so each one can be hovered, selected and opened the
 * way it can on the page canvas. Scoped to the focused Section's own subtree -
 * nothing outside it is on the canvas.
 *
 * The overlays are stacked by containment, so a nested entity paints over its
 * ancestor and stays clickable, while the ancestor keeps whatever area its
 * children don't cover.
 *
 * Read from the node tree rather than the PRFile's frame list: that list only
 * holds frames whose Section ancestry terminates at a page, which a Preview's
 * presentation file doesn't give us, and the frames inside a Section would go
 * unoverlaid - invisible to hover, selection and navigation.
 */
export function getSectionOverlayEntities(
  sectionNode: PRMarinaNode | null
): OverlayEntity[] {
  if (!sectionNode) {
    return []
  }

  const entities: OverlayEntity[] = []

  const collectFrom = (node: PRMarinaNode) => {
    for (const child of convertEmbindVectorToArray(node.getChildren())) {
      if (child.isSection()) {
        entities.push(toOverlayEntity(child))

        // Keep going: the Sections and frames inside it are overlaid too.
        collectFrom(child)
        continue
      }

      // isFrame() only matches the Artboard layer type, which a variant
      // inside a Variant Set isn't - each variant is layer-typed SymbolMaster
      // instead. isTopLevelFrame() catches those too (it's trait- and
      // ancestry-based rather than a raw type check), matching how
      // usePRFileAllFrames() already includes symbol masters on the plain
      // page canvas - without it, variants were silently skipped here and
      // recursed into instead, leaving them without a title overlay.
      if (child.isFrame() || child.isTopLevelFrame()) {
        entities.push(toOverlayEntity(child))
        continue
      }

      // A group holds its contents as its own - a frame inside one is not an
      // entity that can be opened, and the API does not know it as a frame, so
      // there is nothing to overlay in there.
      if (child.isGroup()) {
        continue
      }

      // An internal rendering node (Clip, Maskees, Group Content) carries no
      // layer of its own but does nest the entities we're after.
      collectFrom(child)
    }
  }

  collectFrom(sectionNode)

  return entities
}
